import { useState } from 'react'
import { gql } from 'graphql-request'
import { ClockIcon } from '@heroicons/react/24/outline'
import { useForm, FormProvider } from 'react-hook-form'
import { useInfiniteQuery } from '@tanstack/react-query'

import { useCurrentUser } from '@contexts/currentUser'
import { useMutation } from '@hooks/graphql'
import { request } from '@helpers/graphql'
import { useBreakpoint } from '@contexts/breakpoints'
import Button from '@components/Button'
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog'
import EmptyChat from '@components/EmptyChat'

import ChatList from './ChatList'
import ChatDetails from './ChatDetails'

const CHATS_QUERY = gql`
  query chats($page: Int, $tutorId: ID!) {
    chats(page: $page, perPage: 10, tutorId: $tutorId) {
      nodes {
        id
        createdAt
      }
      pagesCount
      nodesCount
    }
  }
`

const CREATE_CHAT_MUTATION = gql`
  mutation createChat($tutorId: ID!) {
    createChat(input: { tutorId: $tutorId }) {
      chat {
        id
        createdAt
      }
    }
  }
`

const TutorConversation = ({ tutorId, name, imageUrl, imageGenerationStatus, imageGenerationStartedAt }) => {
  const { currentMembership: { organization: { id: organizationId } = {} } = {} } = useCurrentUser()
  const [selectedChatId, setSelectedChatId] = useState(null)
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
  const isMobile = useBreakpoint('sm')

  const methods = useForm({ mode: 'onTouched', defaultValues: { message: null, attachments: [] } })

  const {
    fetchNextPage,
    hasNextPage,
    isLoading,
    refetch,
    data: { pages = [] } = {},
    isFetching
  } = useInfiniteQuery({
    queryKey: ['chats', tutorId],
    queryFn: async ({ pageParam = 1 }) => request(CHATS_QUERY, { tutorId, page: pageParam }, { 'X-Organization-Id': organizationId }),
    onSuccess: (data) => {
      const recentChat = data.pages[0].chats.nodes[0]

      if (!selectedChatId) {
        setSelectedChatId(recentChat?.id)
      }
    },
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.chats.pagesCount > pages.length) {
        return pages.length + 1
      }

      return false
    }
  })

  const {
    mutate: createChat,
    isLoading: isCreating
  } = useMutation({
    gqlMutation: CREATE_CHAT_MUTATION,
    onSuccess: data => {
      refetch()
      setSelectedChatId(data.createChat.chat.id)
    }
  })

  const createTutorChat = () => createChat({ tutorId })

  return (
    <>
      <Choose>
        <When condition={isMobile}>
          <div className='relative flex flex-col h-full bg-white overflow-hidden'>
            <Button
              label={<span className='flex items-center'><ClockIcon className='h-6 w-6 mr-2' />History</span>}
              variant='outlined'
              theme='light'
              className='shadow-lg absolute z-20 top-0 left-0 w-fit m-3'
              onClick={() => setIsHistoryModalOpen(true)}
            />

            <Choose>
              <When condition={selectedChatId}>
                <FormProvider {...methods}>
                  <ChatDetails
                    name={name}
                    tutorId={tutorId}
                    chatId={selectedChatId}
                  />
                </FormProvider>
              </When>

              <When condition={!isLoading && pages?.[0]?.chats?.pagesCount === 0}>
                <EmptyChat name={name} createChat={createTutorChat} />
              </When>
            </Choose>
          </div>

          <Dialog open={isHistoryModalOpen} onOpenChange={setIsHistoryModalOpen}>
            <DialogContent className="sm:modal-middle modal-bottom">
              <DialogTitle>Chat history</DialogTitle>
              <ChatList
                className="p-0"
                tutorId={tutorId}
                imageGenerationStatus={imageGenerationStatus}
                imageGenerationStartedAt={imageGenerationStartedAt}
                selectedChatId={selectedChatId}
                setSelectedChatId={setSelectedChatId}
                pages={pages}
                createChat={createTutorChat}
                isCreating={isCreating}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetching={isFetching}
                refetch={refetch}
              />
            </DialogContent>
          </Dialog>
        </When>

        <Otherwise>
          <div className='flex h-full overflow-hidden'>
            <ChatList
              tutorId={tutorId}
              imageUrl={imageUrl}
              imageGenerationStatus={imageGenerationStatus}
              imageGenerationStartedAt={imageGenerationStartedAt}
              selectedChatId={selectedChatId}
              setSelectedChatId={setSelectedChatId}
              pages={pages}
              createChat={createTutorChat}
              isCreating={isCreating}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetching={isFetching}
              refetch={refetch}
            />

            <Choose>
              <When condition={selectedChatId}>
                <FormProvider {...methods}>
                  <div className="flex-grow h-full overflow-hidden">
                    <ChatDetails
                      name={name}
                      tutorId={tutorId}
                      chatId={selectedChatId}
                    />
                  </div>
                </FormProvider>
              </When>

              <When condition={!isLoading && pages?.[0]?.chats?.pagesCount === 0}>
                <EmptyChat name={name} createChat={createTutorChat} />
              </When>
            </Choose>
          </div>
        </Otherwise>
      </Choose>
    </>
  )
}

export default TutorConversation
